import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GitHubIcon from '@mui/icons-material/GitHub';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  getSurfaceBorder,
  getSurfaceColor,
  radii,
} from '../../../theme/tokens';
import { useHomeIdentity } from '../hooks/useHomeIdentity';

function getTeamSlugs(ownershipEntityRefs: string[]): string[] {
  const slugs = new Set<string>();
  for (const ref of ownershipEntityRefs) {
    const match = ref.match(/^group:[^/]+\/(.+)$/);
    if (match) slugs.add(match[1]);
  }
  return Array.from(slugs).sort((a, b) => a.localeCompare(b));
}

function formatTeamName(slug: string): string {
  return slug
    .split(/[-_]/g)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export const QuickLinks = () => {
  const theme = useTheme();
  const { loading, value: identity } = useHomeIdentity();

  const teams = identity ? getTeamSlugs(identity.ownershipEntityRefs) : [];

  return (
    <Paper sx={{ px: 3, py: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Your teams</Typography>

        {loading ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={44} />
          </Stack>
        ) : (
          <Box
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.lg}px`,
              px: 2.5,
              py: 0.5,
            }}
          >
            <Stack divider={<Divider flexItem />} spacing={0}>
              <Link
                color="inherit"
                component={RouterLink}
                to={`/teams?filters[membership]=${encodeURIComponent(identity?.userEntityRef ?? '')}`}
                underline="none"
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1.5,
                  py: 1.5,
                  textDecoration: 'none',
                }}
              >
                <GroupsOutlinedIcon
                  sx={{ color: 'primary.main', fontSize: 20 }}
                />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  My Teams
                </Typography>
                <ChevronRightIcon
                  sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }}
                />
              </Link>

              {teams.map(slug => (
                <Link
                  key={slug}
                  href={`https://github.com/orgs/meltwater/teams/${slug}/repositories`}
                  target="_blank"
                  rel="noreferrer"
                  color="inherit"
                  underline="none"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1.5,
                    py: 1.5,
                    textDecoration: 'none',
                  }}
                >
                  <GitHubIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.35 }}>
                      {formatTeamName(slug)}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{ lineHeight: 1.3 }}
                    >
                      GitHub repos
                    </Typography>
                  </Box>
                  <LaunchRoundedIcon
                    sx={{
                      color: 'text.disabled',
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  />
                </Link>
              ))}

              {teams.length === 0 && !loading && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ py: 1.5 }}
                >
                  No team memberships found.
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
